import * as Bool from "fp-ts/es6/boolean"
import * as E from "fp-ts/es6/Either"
import { flow, pipe } from "fp-ts/es6/function"
import * as F from "fp-ts/es6/function"
import * as IO from "fp-ts/es6/IO"
import * as Opt from "fp-ts/es6/Option"
import * as R from "fp-ts/es6/Reader"
import * as Arr from "fp-ts/es6/ReadonlyArray"
import * as NonEmpty from "fp-ts/es6/ReadonlyNonEmptyArray"
import * as T from "fp-ts/es6/Task"
import * as TE from "fp-ts/es6/TaskEither"
import * as O from "fp-ts-reactive/es6/Observable"
import { Getter, Iso } from "monocle-ts"
import { dateAsTime, isoStringAsTime } from "time-ts/es6"
import {
  arrayToObservable,
  bisequencePair,
  liftGetter2C,
  share,
  stringAsEmailAddress,
  stringAsUrl,
  unsafeFromSome,
  withLatestFrom,
} from "@fitnesspilot/data-common"

import { LoginResponse } from "@fitnesspilot/api-combined"
import api, {
  Action as APIAction,
  AnyAPIError,
  API,
  APIError,
  setAccountReady,
  setToken,
  stringAsToken,
} from "@fitnesspilot/data-api"

import { GoogleApi } from "../GoogleApi"
import { defaultJob, Job } from "../Job"
import { Photo, PhotoId } from "../photo"
import { stringAsUserId } from "../User"
import { stringAsUserRole, UserClaims } from "../UserClaims"
import {
  Action,
  addInviteCodeAsync,
  changePasswordAsync,
  deleteAccountAsync,
  fetchAboutMeAnswersAsync,
  fetchBody,
  fetchGoogleFitAsync,
  fetchInviteCodesAsync,
  fetchJobsAsync,
  fetchPhotosAsync,
  fetchReferralCodeAsync,
  fetchUnitSelectionAsync,
  fetchUserAsync,
  fetchUserDataAsync,
  fetchWorkAndLifestyle,
  loginAsync,
  logoutAsync,
  requestFcmTokenAsync,
  resetPasswordAsync,
  resetState,
  savePhotoAsync,
  sendFcmToken,
  sendFcmTokenAsync,
  sendGoogleAuthCodeAsync,
  setBody,
  setGoogleFitAsync,
  setHabits,
  setProfilePhotoAsync,
  setUnitSelectionAsync,
  setUserAsync,
  setUserClaims,
  setUserDataAsync,
  setWorkAndLifestyle,
  shareAsync,
  sharePhotoAsync,
  signupAsync,
  submitSetupAsync,
  uploadPhotosAsync,
  uploadProfilePhoto,
} from "./actions"
import * as apiToState from "./api"
import { fileToPhotoUpload } from "./internal"
import * as selectors from "./selectors"
import { ParentState } from "./state"

import "firebase/compat/auth"
import * as Sentry from "@sentry/react"
import * as jsonPatch from "fast-json-patch"
import firebase from "firebase/compat/app"
import * as HTTPStatusCode from "http-status-codes"
import { AnyAction } from "redux"
import { push as pushHistory, RouterActions } from "redux-first-history"
import { combineEpics, Epic } from "redux-observable"
import { forkJoin, Observable } from "rxjs"
import {
  concatMap,
  exhaustMap,
  flatMap,
  switchMap,
  toArray,
} from "rxjs/operators"
import { isActionOf } from "typesafe-actions"

type TaskEitherRightV<a extends TE.TaskEither<any, any>> =
  a extends TE.TaskEither<any, infer v> ? v : never
// Should prolly solve this differently, but that's @TODO
// Note that the auto-generated type has optional fields; this must be because of C# or something
type PhotoDownload = TaskEitherRightV<
  ReturnType<API["photos"]["getPhotos"]>
>[number]
const photoDownloadAsPhoto = new Iso<PhotoDownload, Photo>(
  ({ id, contentUrl, dateCreated }) => ({
    // We can trust this source
    // It's not really optional
    id: id as PhotoId,
    // It's not really optional
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    contentUrl: new URL(contentUrl),
    // It's not really optional
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    dateCreated: pipe(dateCreated, dateAsTime.getOption, unsafeFromSome),
    // There's no description
    description: Opt.none,
  }),
  ({ contentUrl, dateCreated, ...a }) => ({
    contentUrl: String(contentUrl),
    dateCreated: dateAsTime.reverseGet(dateCreated),
    ...a,
  }),
)

type PhotoUpload = {
  dateCreated: string
}
const photoAsPhotoUpload = new Getter<Photo, PhotoUpload>(
  ({ dateCreated }) => ({
    dateCreated: isoStringAsTime.reverseGet(dateCreated),
  }),
)

type AnEpic = Epic<
  Action | APIAction | RouterActions | AnyAction,
  Action | APIAction | RouterActions,
  ParentState
>

export const firebaseLoginFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(
      (
        action,
      ): action is {
        type: "@@reactReduxFirebase/LOGIN"
        auth: firebase.User
      } => action.type === "@@reactReduxFirebase/LOGIN",
    ),
    withLatestFrom(state$),
    O.chain(([, { router }]) =>
      pipe(
        [
          pipe(loginAsync.success(), T.of, Opt.some),
          pipe(
            router.location?.state as { from: string } | undefined,
            Opt.fromNullable,
            Opt.chain(flow((v) => v.from, Opt.fromNullable)),
            Opt.map(flow(pushHistory, T.of)),
          ),
        ] as ReadonlyArray<
          Opt.Option<T.Task<Action | APIAction | RouterActions>>
        >,
        Arr.compact,
        Arr.sequence(T.ApplicativeSeq),
        O.fromTask,
        (a) => a,
        O.chain((as) => arrayToObservable(as)),
      ),
    ),
  )

export const firebaseNotLoggedInFlow: AnEpic = (action$) =>
  pipe(
    action$,
    O.filter(
      (
        action,
      ): action is {
        type: "@@reactReduxFirebase/AUTH_EMPTY_CHANGE"
      } => action.type === "@@reactReduxFirebase/AUTH_EMPTY_CHANGE",
    ),
    O.map(() => [logoutAsync.success(), setToken(Opt.none)] as Array<Action>),
    O.chain(arrayToObservable),
  )

export const signupFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(signupAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([{ email, password, inviteCode }, api]) =>
      pipe(
        api.users.signup({
          userAccount: {
            email: stringAsEmailAddress.reverseGet(email),
            password,
            inviteCode: Opt.toUndefined(inviteCode),
            applyDemoData: false,
            googleApiActive: false,
          },
        }),
        TE.fold<AnyAPIError, LoginResponse, Action>(
          flow(signupAsync.failure, T.of),
          () => pipe(signupAsync.success({ email, password }), T.of),
        ),
        O.fromTask,
      ),
    ),
  )

export const submitSetupFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(submitSetupAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(
      ([
        {
          units,
          body,
          work,
          sleep,
          habits,
          generalSportsSettings,
          enableNutritionRecommendations,
        },
        api,
      ]) =>
        pipe(
          api.users.setup({
            units: apiToState.unitSelection.reverseGet(units),
            user: {
              userdata: apiToState.userData(units).reverseGet({
                body,
                work,
                sleep,
                habits,
              }),
            },
            activityRecommendations:
              generalSportsSettings.enableExerciseRecommendations,
            nutritionRecommendations: enableNutritionRecommendations,
          })("me"),
          TE.fold<AnyAPIError, void, Action>(
            flow(submitSetupAsync.failure, T.of),
            () => pipe(submitSetupAsync.success(), T.of),
          ),
          O.fromTask,
        ),
    ),
  )

export const submitSetupSuccessFlow: AnEpic = (
  action$,
  // state$
) =>
  pipe(
    action$,
    O.filter(isActionOf(submitSetupAsync.success)),
    O.map((a) => a.payload),
    O.chain(() => O.of(loginAsync.success())),
  )

export const signupSuccessFlow: AnEpic = (
  action$,
  // state$,
) =>
  pipe(
    action$,
    O.filter(isActionOf(signupAsync.success)),
    O.map((a) => a.payload),
    O.chain(({ email, password }) =>
      O.of(loginAsync.request({ email, password })),
    ),
  )

export const loginFlow: AnEpic = (
  action$,
  // state$,
) =>
  pipe(
    action$,
    O.filter(isActionOf(loginAsync.request)),
    O.chain(
      flow(
        ({ payload: { email, password } }) =>
          TE.tryCatch(
            () =>
              firebase
                .app()
                .auth()
                .signInWithEmailAndPassword(
                  stringAsEmailAddress.reverseGet(email),
                  password,
                ),
            (e: unknown): firebase.FirebaseError => e as any,
          ),
        TE.fold<
          firebase.FirebaseError,
          firebase.auth.UserCredential,
          ReadonlyArray<Action>
        >(flow(loginAsync.failure, Arr.of, T.of), () => T.of([])),
        O.fromTask,
        O.chain(arrayToObservable),
      ),
    ),
  )

export const loginSuccessFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(loginAsync.success)),
    O.map((a) => a.payload),
    O.chain(() =>
      pipe(
        TE.tryCatch(
          () =>
            firebase.app().auth().currentUser?.getIdTokenResult?.() ??
            Promise.reject(new Error("Not logged in")),
          (e: unknown): firebase.FirebaseError | Error => e as any,
        ),
        TE.fold(
          // @TODO This shouldn't trigger loginAsync.failure
          flow((e): Action | APIAction => loginAsync.failure(e), Arr.of, T.of),
          flow(
            ({ token, claims }) => [
              pipe(token, stringAsToken.getOption, setToken),
              pipe(
                {
                  role: pipe(claims.role as string, stringAsUserRole.getOption),
                  admin: claims.admin === true,
                } as UserClaims,
                Opt.some,
                setUserClaims,
              ),
            ],
            T.of,
          ),
        ),
        O.fromTask,
        O.chain(arrayToObservable),
      ),
    ),
  )

export const logoutFlow: AnEpic = (
  action$,
  // state$,
) =>
  pipe(
    action$,
    O.filter(isActionOf(logoutAsync.request)),
    O.chain(() => pipe(() => firebase.app().auth().signOut(), O.fromTask)),
    O.filter((v: unknown): v is Action => false),
  )

export const setTokenFlow: AnEpic = (
  action$,
  // state$,
) =>
  pipe(
    action$,
    O.filter(isActionOf(setToken)),
    O.map((a) => a.payload),
    O.map(
      Opt.fold<any, ReadonlyArray<Action>>(
        flow(
          resetState,
          Arr.of,
          IO.of,
          IO.apFirst(() =>
            Sentry.configureScope((scope) => scope.setUser(null)),
          ),
        )(),
        () => [fetchUserAsync.request()],
      ),
    ),
    O.chain(arrayToObservable),
  )

export const setAccountReadyFlow: AnEpic = (
  action$,
  // state$,
) =>
  pipe(
    action$,
    O.filter(isActionOf(setAccountReady)),
    O.map((a) => a.payload),
    O.map(
      Bool.fold(
        () => [],
        () => [
          fetchPhotosAsync.request(),
          fetchGoogleFitAsync.request(),
          fetchUnitSelectionAsync.request(),
          fetchAboutMeAnswersAsync.request(),
          fetchReferralCodeAsync.request(),
        ],
      ),
    ),
    O.chain(arrayToObservable),
  )

export const setUserRoleFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(setUserClaims)),
    O.map((a) => a.payload),
    withLatestFrom(state$),
    O.map(([token, state]) =>
      pipe(
        token,
        Opt.fold<any, ReadonlyArray<Action>>(flow(resetState, Arr.of), () =>
          pipe(
            state,
            selectors.state.compose(selectors.userClaims).get,
            IO.of,
            IO.chainFirst(
              Opt.fold<UserClaims, IO.IO<void>>(
                () => IO.of(undefined),
                (claims) => () =>
                  Sentry.configureScope((scope) =>
                    scope.setUser({
                      ...scope.getUser(),
                      role: pipe(
                        claims.role,
                        Opt.fold(() => undefined, stringAsUserRole.reverseGet),
                      ),
                      admin: claims.admin,
                    }),
                  ),
              ),
            ),
            IO.chain(
              flow(
                Opt.fold(
                  () => false,
                  (claims) => claims.admin === true,
                ),
                Bool.fold(
                  () => [],
                  () => [fetchInviteCodesAsync.request()],
                ),
                IO.of,
              ),
            ),
          )(),
        ),
      ),
    ),
    O.chain(arrayToObservable),
  )

export const requestFcmTokenSuccessFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(requestFcmTokenAsync.success)),
    O.map((a) => a.payload),
    withLatestFrom(state$),
    O.chain(([token, state]) =>
      pipe(
        state,
        selectors.state.compose(selectors.fcmToken).get,
        Opt.map((t) => (t === token ? Opt.none : Opt.some(token))),
        Opt.getOrElse(() => Opt.some(token)),
        Opt.fold(
          () => [],
          (t) => [sendFcmToken(t)],
        ),
        arrayToObservable,
      ),
    ),
  )

export const sendFcmTokenFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(sendFcmToken)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([token, api]) =>
      pipe(
        api.users.sendFcmToken(token)("me"),
        TE.fold(
          flow((e): Action => sendFcmTokenAsync.failure(e), T.of),
          flow(() => pipe(token, sendFcmTokenAsync.success), T.of),
        ),
        O.fromTask,
      ),
    ),
  )

export const fetchUserFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(fetchUserAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([, api]) =>
      pipe(
        api.users.getAccount("me"),
        TE.fold(
          flow((e): Action => fetchUserAsync.failure(e), T.of),
          flow(
            (account) =>
              pipe(account, apiToState.user.get, fetchUserAsync.success),
            T.of,
          ),
        ),
        O.fromTask,
      ),
    ),
  )

export const setUserFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(setUserAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([user, api]) =>
      pipe(
        pipe(user, apiToState.user.reverseGet, api.users.updateAccount)("me"),
        TE.fold(
          flow((e): Action => setUserAsync.failure(e), T.of),
          flow(apiToState.user.get, setUserAsync.success, T.of),
        ),
        O.fromTask,
      ),
    ),
  )

export const setUserSuccessFlow: AnEpic = (
  action$,
  // state$,
) =>
  pipe(
    action$,
    O.filter(isActionOf(setUserAsync.success)),
    O.map((a) => a.payload),
    O.chain((user) => O.of(fetchUserAsync.success(user))),
  )

export const fetchUserSuccessFlow: AnEpic = (
  action$,
  // state$,
) =>
  pipe(
    action$,
    O.filter(isActionOf(fetchUserAsync.success)),
    O.chain(({ payload: user }) =>
      pipe(
        pipe(
          IO.Do,
          IO.map(() => [] as ReadonlyArray<Action | APIAction>),
          IO.apFirst(() =>
            Sentry.configureScope((scope) =>
              scope.setUser({
                ...scope.getUser(),
                id: pipe(user.id, stringAsUserId.reverseGet),
              }),
            ),
          ),
        )(),
        Arr.concat<Action | APIAction>([
          setAccountReady(user.hasCompletedSetup),
        ]),
        (as) => arrayToObservable(as as Array<Action>),
      ),
    ),
  )

export const fetchUserDataFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(fetchUserDataAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([, api]) =>
      pipe(
        api.users.getUser("me"),
        TE.fold(
          flow((e): Action => fetchUserDataAsync.failure(e), T.of),
          flow(
            apiToState.userData(state$.value.user.unitSelection).get,
            fetchUserDataAsync.success,
            T.of,
          ),
        ),
        O.fromTask,
      ),
    ),
  )

export const setUserDataFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(setUserDataAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([userData, api]) =>
      pipe(
        pipe(
          userData,
          apiToState.userData(state$.value.user.unitSelection).reverseGet,
          api.users.updateUser,
        )("me"),
        TE.fold(
          flow((e): Action => setUserDataAsync.failure(e), T.of),
          flow(
            apiToState.userData(state$.value.user.unitSelection).get,
            setUserDataAsync.success,
            T.of,
          ),
        ),
        O.fromTask,
      ),
    ),
  )

export const setUserDataSuccessFlow: AnEpic = (
  action$,
  // state$,
) =>
  pipe(
    action$,
    O.filter(isActionOf(setUserDataAsync.success)),
    O.map((a) => a.payload),
    O.chain((userData) => O.of(fetchUserDataAsync.success(userData))),
  )

export const setHabitsFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(setHabits)),
    O.map((a) => a.payload),
    withLatestFrom(state$),
    O.map(([habits, state]) =>
      setUserDataAsync.request({
        body: pipe(state, selectors.state.composeLens(selectors.body).get),
        work: pipe(
          state,
          selectors.state.composeLens(selectors.work).get,
          Opt.some,
        ),
        sleep: pipe(
          state,
          selectors.state.composeLens(selectors.sleep).get,
          Opt.some,
        ),
        habits,
      }),
    ),
  )

export const fetchBodyFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(fetchBody)),
    O.map((a) => a.payload),
    withLatestFrom(state$),
    O.map(([, state]) => fetchUserDataAsync.request()),
  )

export const setBodyFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(setBody)),
    O.map((a) => a.payload),
    withLatestFrom(state$),
    O.map(([body, state]) =>
      setUserDataAsync.request({
        body,
        work: pipe(
          state,
          selectors.state.composeLens(selectors.work).get,
          Opt.some,
        ),
        sleep: pipe(
          state,
          selectors.state.composeLens(selectors.sleep).get,
          Opt.some,
        ),
        habits: pipe(state, selectors.state.composeLens(selectors.habits).get),
      }),
    ),
  )

export const fetchWorkAndLifestyleFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(fetchWorkAndLifestyle)),
    O.map((a) => a.payload),
    withLatestFrom(state$),
    O.map(([, state]) => fetchUserDataAsync.request()),
  )

export const setWorkAndLifestyleFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(setWorkAndLifestyle)),
    O.map((a) => a.payload),
    withLatestFrom(state$),
    O.map(([{ work, sleep, habits }, state]) =>
      setUserDataAsync.request({
        body: pipe(state, selectors.state.composeLens(selectors.body).get),
        work,
        sleep,
        habits,
      }),
    ),
  )

export const fetchUnitSelectionFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(fetchUnitSelectionAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([, api]) =>
      pipe(
        api.intl.getUnits("me"),
        TE.fold(
          flow((e): Action => fetchUnitSelectionAsync.failure(e), T.of),
          flow(
            apiToState.unitSelection.get,
            fetchUnitSelectionAsync.success,
            T.of,
          ),
        ),
        O.fromTask,
      ),
    ),
  )

export const fetchUnitSelectionSuccessFlow: AnEpic = (action$) =>
  pipe(
    action$,
    O.filter(isActionOf(fetchUnitSelectionAsync.success)),
    O.map(() => [fetchUserDataAsync.request()]),
    O.chain(arrayToObservable),
  )

export const setUnitSelectionFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(setUnitSelectionAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([unitSelection, api]) =>
      pipe(
        pipe(
          unitSelection,
          apiToState.unitSelection.reverseGet,
          api.intl.updateUnits,
        )("me"),
        TE.fold(
          flow((e): Action => setUnitSelectionAsync.failure(e), T.of),
          flow(
            apiToState.unitSelection.get,
            setUnitSelectionAsync.success,
            T.of,
          ),
        ),
        O.fromTask,
      ),
    ),
  )

export const fetchAboutMeAnswersFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(fetchAboutMeAnswersAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([, api]) =>
      pipe(
        api.users.getAboutMeSummary("me"),
        TE.fold(
          flow((e): Action => fetchAboutMeAnswersAsync.failure(e), T.of),
          flow(
            apiToState.aboutMeAnswers.get,
            fetchAboutMeAnswersAsync.success,
            T.of,
          ),
        ),
        O.fromTask,
      ),
    ),
  )

export const changePasswordFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(changePasswordAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(state$),
    O.chain(([{ password }, state]) =>
      pipe(
        TE.tryCatch(
          () =>
            firebase.app().auth().currentUser?.updatePassword?.(password) ??
            Promise.resolve(undefined),
          (e: unknown): firebase.FirebaseError => e as any,
        ),
        TE.fold<firebase.FirebaseError, void, ReadonlyArray<Action>>(
          flow(loginAsync.failure, Arr.of, T.of),
          () => T.of([]),
        ),
        O.fromTask,
        O.chain(arrayToObservable),
      ),
    ),
  )

export const resetPasswordFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(resetPasswordAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(state$),
    O.chain(([{ email }, state]) =>
      pipe(
        TE.tryCatch(
          () =>
            firebase
              .app()
              .auth()
              .sendPasswordResetEmail(
                pipe(email, stringAsEmailAddress.reverseGet),
              ),
          (e: unknown): firebase.FirebaseError => e as any,
        ),
        TE.fold<firebase.FirebaseError, void, ReadonlyArray<Action>>(
          flow(loginAsync.failure, Arr.of, T.of),
          () => T.of([]),
        ),
        O.fromTask,
        O.chain(arrayToObservable),
      ),
    ),
  )

export const fetchPhotosFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(fetchPhotosAsync.request)),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([, api]) =>
      pipe(
        [
          api.photos.getPhotos("me"),
          pipe(
            api.photos.getProfilePhoto("me"),
            TE.fold<
              AnyAPIError,
              string,
              E.Either<AnyAPIError, Opt.Option<string>>
            >(
              (e) =>
                e instanceof APIError &&
                e.statusCode === HTTPStatusCode.NOT_FOUND
                  ? TE.right(Opt.none)
                  : TE.left(e),
              (v) => TE.right(Opt.some(v)),
            ),
          ),
        ] as const,
        bisequencePair(TE.ApplyPar),
        TE.map(
          ([photos, optProfilePhotoUrl]): [
            ReadonlyArray<Photo>,
            Opt.Option<Photo>,
          ] => [
            Arr.map(photoDownloadAsPhoto.get)(photos),
            pipe(
              optProfilePhotoUrl,
              Opt.chain<string, PhotoDownload>((profilePhotoUrl) =>
                Arr.findFirst<PhotoDownload>(
                  (photo) => photo.contentUrl === profilePhotoUrl,
                )(photos),
              ),
              Opt.map(photoDownloadAsPhoto.get),
            ),
          ],
        ),
        TE.fold<AnyAPIError, [ReadonlyArray<Photo>, Opt.Option<Photo>], Action>(
          flow(fetchPhotosAsync.failure, T.of),
          flow(
            ([photos, profilePhoto]) =>
              fetchPhotosAsync.success({ photos, profilePhoto }),
            T.of,
          ),
        ),
        O.fromTask,
      ),
    ),
  )

export const uploadPhotosFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(uploadPhotosAsync.request)),
    O.chain(
      flow(
        (a) => a.payload,
        Arr.map(fileToPhotoUpload),
        Arr.sequence(T.ApplicativeSeq),
        O.fromTask,
      ),
    ),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([bodies, api]) =>
      pipe(
        bodies,
        Arr.traverse(R.reader)(api.photos.addPhoto),
        (f) => f("me"),
        Arr.sequence(TE.ApplicativeSeq),
        TE.map(Arr.map(photoDownloadAsPhoto.get)),
        TE.fold<AnyAPIError, ReadonlyArray<Photo>, Action>(
          flow(uploadPhotosAsync.failure, T.of),
          flow(uploadPhotosAsync.success, T.of),
        ),
        O.fromTask,
      ),
    ),
  )

export const setProfilePhotoFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(setProfilePhotoAsync.request)),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([{ payload: photo }, api]) =>
      flow(
        Opt.map((v: Photo) => v.id),
        Opt.fold(
          () => api.photos.removeProfilePhoto("me"),
          (photo) => api.photos.setProfilePhoto(photo)("me"),
        ),
        TE.map(F.constant(photo)),
        TE.fold<AnyAPIError, Opt.Option<Photo>, Action>(
          flow(setProfilePhotoAsync.failure, T.of),
          flow(setProfilePhotoAsync.success, T.of),
        ),
        O.fromTask,
      )(photo),
    ),
  )

export const uploadProfilePhotoFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(uploadProfilePhoto)),
    O.chain(flow((a) => a.payload, fileToPhotoUpload, O.fromTask)),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([body, api]) =>
      pipe(
        body,
        api.photos.addPhoto,
        (f) => f("me"),
        TE.map(photoDownloadAsPhoto.get),
        TE.fold<AnyAPIError, Photo, ReadonlyArray<Action>>(
          flow(uploadPhotosAsync.failure, Arr.of, T.of),
          (photo) =>
            pipe(
              [
                pipe(photo, Arr.of, uploadPhotosAsync.success, T.of),
                pipe(
                  api.photos.setProfilePhoto(photo.id),
                  (f) => f("me"),
                  TE.map(() => pipe(photo, Opt.some)),
                  TE.fold<AnyAPIError, Opt.Option<Photo>, Action>(
                    flow(setProfilePhotoAsync.failure, T.of),
                    flow(setProfilePhotoAsync.success, T.of),
                  ),
                ),
              ],
              Arr.sequence(T.ApplicativeSeq),
            ),
        ),
        O.fromTask,
        O.chain(arrayToObservable),
      ),
    ),
  )

export const savePhotoFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(savePhotoAsync.request)),
    withLatestFrom(state$),
    O.chain(([{ payload: photo }, state]) =>
      pipe(
        api(state).photos.updatePhoto(
          photo.id,
          photoDownloadAsPhoto.reverseGet(photo),
        )("me"),
        TE.map(photoDownloadAsPhoto.get),
        TE.fold<AnyAPIError, Photo, Action>(
          flow(savePhotoAsync.failure, T.of),
          flow(savePhotoAsync.success, T.of),
        ),
        O.fromTask,
      ),
    ),
  )

export const sharePhotoFlow: AnEpic = (action$) =>
  pipe(
    action$,
    O.filter(isActionOf(sharePhotoAsync.request)),
    O.chain(({ payload: photo }) =>
      pipe(
        photo,
        (v: Photo) => v.contentUrl,
        stringAsUrl.reverseGet,
        (url) => share({ url }),
        TE.fold<Error, void, Action>(
          flow(sharePhotoAsync.failure, T.of),
          flow(sharePhotoAsync.success, T.of),
        ),
        O.fromTask,
      ),
    ),
  )

export const shareReferralCodeFlow: AnEpic = (action$) =>
  pipe(
    action$,
    O.filter(isActionOf(shareAsync.request)),
    O.chain(({ payload }) =>
      pipe(
        share(payload),
        TE.fold<Error, void, Action>(
          flow(shareAsync.failure, T.of),
          flow(shareAsync.success, T.of),
        ),
        O.fromTask,
      ),
    ),
  )

export const deleteAccountFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(deleteAccountAsync.request)),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([_, api]) =>
      pipe(
        api.users.deleteAccount("me"),
        // @TODO Should we delete the firebase auth user in here?
        TE.fold(
          flow((e): Action => deleteAccountAsync.failure(e), T.of),
          flow(() => deleteAccountAsync.success(), T.of),
        ),
        O.fromTask,
      ),
    ),
  )

export const deleteAccountSuccessFlow: AnEpic = (
  action$,
  // state$,
) =>
  pipe(
    action$,
    O.filter(isActionOf(deleteAccountAsync.success)),
    O.map((a) => a.payload),
    O.chain(() => O.of(logoutAsync.request())),
  )

export const fetchJobsFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(fetchJobsAsync.request)),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([, api]) =>
      pipe(
        api.jobs.getAll("me"),
        TE.map(Arr.map(apiToState.activityJob.get)),
        TE.fold<AnyAPIError, ReadonlyArray<Job>, Action>(
          flow(fetchJobsAsync.failure, T.of),
          flow(
            NonEmpty.fromReadonlyArray,
            Opt.getOrElse(
              (): NonEmpty.ReadonlyNonEmptyArray<Job> => [defaultJob],
            ),
            (jobs) => ({ jobs }),
            fetchJobsAsync.success,
            T.of,
          ),
        ),
        O.fromTask,
      ),
    ),
  )

export const sendGoogleAuthCodeFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(sendGoogleAuthCodeAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([authCode, api]) =>
      pipe(
        pipe(
          authCode,
          apiToState.googleAuthCodeToApi.get,
          (authorizationCodeRequest) =>
            api.googleApi.setAuthorisationCode({ authorizationCodeRequest }),
        )("me"),
        TE.fold(
          flow((e): Action => sendGoogleAuthCodeAsync.failure(e), T.of),
          () => pipe(sendGoogleAuthCodeAsync.success(), T.of),
        ),
        O.fromTask,
      ),
    ),
  )

export const fetchGoogleFitFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(fetchGoogleFitAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([, api]) =>
      pipe(
        api.googleFit.get("me"),
        TE.fold(
          flow((e): Action => fetchGoogleFitAsync.failure(e), T.of),
          flow(
            apiToState.googleFit.get,
            Opt.some,
            fetchGoogleFitAsync.success,
            T.of,
          ),
        ),
        O.fromTask,
      ),
    ),
  )

export const setGoogleFitFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(setGoogleFitAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([googleFit, api]) =>
      pipe(
        pipe(
          googleFit,
          Opt.fold(
            () => api.googleFit.delete,
            flow(apiToState.googleFit.reverseGet, (apiSettings) =>
              api.googleFit.set({ apiSettings }),
            ),
          ),
        )("me"),
        TE.fold(
          flow((e): Action => setGoogleFitAsync.failure(e), T.of),
          () => pipe(googleFit, setGoogleFitAsync.success, T.of),
        ),
        O.fromTask,
      ),
    ),
  )

export const fetchReferralCodeFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(fetchReferralCodeAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([, api]) =>
      pipe(
        api.referrals.get("me"),
        TE.fold(
          flow((e): Action => fetchReferralCodeAsync.failure(e), T.of),
          flow(
            apiToState.referralCode.get,
            fetchReferralCodeAsync.success,
            T.of,
          ),
        ),
        O.fromTask,
      ),
    ),
  )

export const fetchInviteCodesFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(fetchInviteCodesAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([, api]) =>
      pipe(
        api.invites.getAll(),
        TE.fold(
          flow((e): Action => fetchInviteCodesAsync.failure(e), T.of),
          flow(
            Arr.map(apiToState.inviteCode.get),
            fetchInviteCodesAsync.success,
            T.of,
          ),
        ),
        O.fromTask,
      ),
    ),
  )

export const addInviteCodeFlow: AnEpic = (action$, state$) =>
  pipe(
    action$,
    O.filter(isActionOf(addInviteCodeAsync.request)),
    O.map((a) => a.payload),
    withLatestFrom(O.map(api)(state$)),
    O.chain(([, api]) =>
      pipe(
        api.invites.add(),
        TE.fold(
          flow((e): Action => addInviteCodeAsync.failure(e), T.of),
          flow(apiToState.inviteCode.get, addInviteCodeAsync.success, T.of),
        ),
        O.fromTask,
      ),
    ),
  )

const epic: AnEpic = combineEpics(
  firebaseLoginFlow,
  firebaseNotLoggedInFlow,
  signupFlow,
  signupSuccessFlow,
  submitSetupFlow,
  submitSetupSuccessFlow,
  loginFlow,
  loginSuccessFlow,
  logoutFlow,
  setTokenFlow,
  setAccountReadyFlow,
  setUserRoleFlow,
  fetchUserFlow,
  fetchWorkAndLifestyleFlow,
  setWorkAndLifestyleFlow,
  setUserFlow,
  setUserSuccessFlow,
  fetchUserSuccessFlow,
  fetchUserDataFlow,
  setUserDataFlow,
  setUserDataSuccessFlow,
  setHabitsFlow,
  fetchBodyFlow,
  setBodyFlow,
  fetchUnitSelectionFlow,
  fetchUnitSelectionSuccessFlow,
  setUnitSelectionFlow,
  fetchAboutMeAnswersFlow,
  changePasswordFlow,
  resetPasswordFlow,
  fetchPhotosFlow,
  uploadPhotosFlow,
  setProfilePhotoFlow,
  uploadProfilePhotoFlow,
  savePhotoFlow,
  deleteAccountFlow,
  deleteAccountSuccessFlow,
  sharePhotoFlow,
  shareReferralCodeFlow,
  fetchJobsFlow,
  requestFcmTokenSuccessFlow,
  sendFcmTokenFlow,
  sendGoogleAuthCodeFlow,
  fetchGoogleFitFlow,
  setGoogleFitFlow,
  fetchReferralCodeFlow,
  fetchInviteCodesFlow,
  addInviteCodeFlow,
)
export default epic
